import imageUrlBuilder from '@sanity/image-url';
import { checkValue } from 'src/components/common/helper';
import { createClient } from '@sanity/client';
import { config } from 'src/config/config';
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';
import { useStores } from '../../utils/useStore';

type options = {
  width?: number;
  height?: number;
  blur?: number;
  focalPoint?: { x: number; y: number };
  crop?: 'center' | 'top' | 'bottom' | 'left' | 'right' | 'focalpoint' | 'entropy';
};

const getClient = () => {
  const { sanityStore } = useStores();
  const client = createClient({
    projectId: config.sanityProjectId,
    dataset: sanityStore.isGoGuides ? config.goGuideDataSet : config.sanityDataSet,
    apiVersion: config.sanityApiVersion,
    useCdn: false,
  });
  return client;
}

export const creatTinyImageUrl = (imageUrl: any) => {
  const client = getClient();
  const builder = imageUrlBuilder(client);

  return builder
    .image(imageUrl)
    .width(400) // adjust as needed for card size
    .height(400)
    .auto('format') // get webp/jpg as needed
    .quality(60) // reduce quality for performance
    .url();
};

const createPosterImageUrl = (baseUrl, width, height) => {
  const url = new URL(baseUrl);
  url.searchParams.set('w', width);
  url.searchParams.set('h', height);
  url.searchParams.set('auto', 'format'); // optional, but keeps the original behavior
  return url.toString();
}

export const getPosterImageUrl = (imageUrl: any) => {
  const thresholdWidth = 767;
  const focalPoint = { x: 0.5, y: 0.5 };
  const defaultOptions = { focalPoint: focalPoint, width: 600, height: 400 };
  const { width: windowWidth } = useWindowDimensions();
  const breakPoints = ['768w', '1280w', '1680w'];
  const smWidth = Math.ceil(768 * 1.2);
  const meWidth = Math.ceil(1280 * 1.2);
  const lgWidth = Math.ceil(1680 * 1.2);
  const sizes = `
(max-width: 768px) 768px, 
(max-width: 1280px) 1280px, 
1680px
`;
  const newImageUrls = [];

  const imageUrl1 = createPosterImageUrl(imageUrl,
    smWidth,
    getHeight(smWidth, '1:1'),
  );
  const imageUrl2 = createPosterImageUrl(imageUrl,
    meWidth,
    getHeight(meWidth, '1:1'),
  );
  const imageUrl3 = createPosterImageUrl(imageUrl,
    lgWidth,
    getHeight(lgWidth, '1:1'),
  );
  //const imageUrl4 = createImageUrl(imageUrl);

  newImageUrls.push(imageUrl1);
  newImageUrls.push(imageUrl2);
  newImageUrls.push(imageUrl3);
  newImageUrls.push(imageUrl);

  const srcSet = generateSrcSet(newImageUrls, breakPoints);
  const imageSet = convertSrcsetToImageSet(srcSet);

  return { imageURL: imageUrl2, srcSet: srcSet, sizes, imageSet: imageSet };
}

const getHeight = (width: number, aspectRatio: string) => {
  const [aspectWidth, aspectHeight] = aspectRatio.split(':').map(Number);
  return Math.round((aspectHeight / aspectWidth) * width);
};

const useGetSanityImageUrl = () => {
  //const { sanityStore } = useStores();  
  // const client = createClient({
  //   projectId: config.sanityProjectId,
  //   dataset: sanityStore.isGoGuides? config.goGuideDataSet : config.sanityDataSet,
  //   apiVersion: config.sanityApiVersion,
  //   useCdn: false,
  // });

  const client = getClient();
  const thresholdWidth = 767;
  const focalPoint = { x: 0.5, y: 0.5 };
  const defaultOptions = { focalPoint: focalPoint, width: 600, height: 400 };
  const { width: windowWidth } = useWindowDimensions();
  const breakPoints = ['768w', '1280w', '1680w'];
  const smWidth = Math.ceil(768 * 1.2);
  const meWidth = Math.ceil(1280 * 1.2);
  const lgWidth = Math.ceil(1680 * 1.2);
  const sizes = `
  (max-width: 768px) 768px, 
  (max-width: 1280px) 1280px, 
  1680px
  `;

  const builder = imageUrlBuilder(client);

  const isValidCrop = (crop) => {
    // Check if any of top, bottom, left, right is not 0
    if ((crop != null || crop != undefined) && (crop?.top !== 0 || crop?.bottom !== 0 || crop?.left !== 0 || crop?.right !== 0)) {
      return true;
    }
    return false;
  };

  const getImageUrl = (source: any, reqSrcSet: boolean = false, options: options = defaultOptions) => {
    const newImageUrls = [];
    const placeholderSrcDefault =
      'https://cdn.sanity.io/images/nxpteyfv/media-studio/4eac2877cf1372bc2ccd04c48bdf8344d6cdc1a9-330x312.png?fit=max&w=250&h=250';

    const isValidSource = checkValue(source?.image?.asset?.url) || checkValue(source?.asset?.url);
    if (!isValidSource) {
      return {
        imageURL: placeholderSrcDefault,
        srcSet: undefined,
        sizes: undefined,
        imageSet: undefined,
      };
    }

    const isImageCrop = isValidCrop(source?.image?.crop || source?.crop);
    
    if (isImageCrop) {
      const imageUrl = builder.image(source?.image || source);
      return createImageUrl(imageUrl);
    } else if (checkValue(source?.mobileImage)) {
      // const imageUrl = createImageUrl(builder.image(source?.image));
      const imageUrl = builder.image(source?.image);
      const imageUrl1 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: smWidth,
        height: getHeight(smWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl2 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: meWidth,
        height: getHeight(meWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl3 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: lgWidth,
        height: getHeight(lgWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl4 = createImageUrl(imageUrl);

      const mobileImageUrl = createImageUrl(builder.image(source?.mobileImage));

      newImageUrls.push(imageUrl1);
      newImageUrls.push(imageUrl2);
      newImageUrls.push(imageUrl3);
      newImageUrls.push(imageUrl4);
      const srcSet = generateSrcSet(newImageUrls, breakPoints);
      const imageSet = convertSrcsetToImageSet(srcSet);   

      if (windowWidth > thresholdWidth) {
        return { imageURL: imageUrl2, srcSet: srcSet, sizes, imageSet: imageSet };
      }
      return { imageURL: mobileImageUrl, srcSet: undefined, sizes: undefined, imageSet: undefined };

      // return windowWidth > thresholdWidth ? imageUrl1 : imageUrl2;
    } else if (reqSrcSet && !isImageCrop) {
      const imageUrl = builder.image(source?.image || source);
  
      const imageUrl1 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: smWidth,
        height: getHeight(smWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl2 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: meWidth,
        height: getHeight(meWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl3 = createImageUrl(imageUrl, {
        focalPoint: focalPoint,
        width: lgWidth,
        height: getHeight(lgWidth, source?.aspectRatio || '3:4'),
      });
      const imageUrl4 = createImageUrl(imageUrl);

      newImageUrls.push(imageUrl1);
      newImageUrls.push(imageUrl2);
      newImageUrls.push(imageUrl3);
      newImageUrls.push(imageUrl4);
      const srcSet = generateSrcSet(newImageUrls, breakPoints);
      const imageSet = convertSrcsetToImageSet(srcSet);

      return { imageURL: imageUrl2, srcSet: srcSet, sizes, imageSet: imageSet };
    } else if (checkValue(source?.image)) {
      const imageUrl = builder.image(source.image);

      return createImageUrl(imageUrl);
    } else {
      const imageUrl = builder.image(source);

      return createImageUrl(imageUrl);
    }
  };

  return { getImageUrl };
};

const createImageUrl = (imageUrl: any, options?: options) => {
  if (checkValue(options) && checkValue(Object.keys(options), true)) {
    Object.keys(options).forEach((key) => {    
      if (checkValue(options?.[key])) {
        if (key === 'focalPoint') {
          imageUrl = imageUrl?.[key](options[key].x, options[key].y);
        } else {
          imageUrl = imageUrl?.[key](options[key]);
        }
      }
    });
  }
  return imageUrl?.auto('format').url();
};

const generateSrcSet = (imageUrls, breakPoints) => {
  return [
    // `${imageUrls[1].replace(/w=\d+/, `w=${threshold}`)} ${threshold}w`, // Second image for small screens
    // `${imageUrls[0].replace(/w=\d+/, `w=1600`)} 1600w`, // Keep first image for large screens

    `${imageUrls[0]} ${breakPoints[0]}`, // Second image for small screens
    `${imageUrls[1]} ${breakPoints[1]}`, // Second image for small screens
    `${imageUrls[2]} ${breakPoints[2]}`, // Keep first image for large screens
  ].join(', ');
};

const convertSrcsetToImageSet = (srcset) => {
  const mappings = srcset.split(',').map((entry) => {
    const [url, width] = entry.trim().split(' ');
    const wValue = parseInt(width.replace('w', ''), 10);

    // Convert width to x-based resolution (assuming base 768w = 1x)
    let xValue;
    if (wValue <= 922) xValue = '1x';
    else if (wValue <= 1536) xValue = '2x';
    else xValue = '3x';

    return `url("${url}") ${xValue}`;
  });

  return `image-set(${mappings.join(', ')});`;
};

export default useGetSanityImageUrl;
